import {
  AdminPanelSettings,
  Analytics,
  ArrowBack,
  ArrowForward,
  Assignment,
  CheckCircleOutline,
  EmailOutlined,
  LockOutlined,
  People,
  School,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Fade,
  InputAdornment,
  TextField,
  Typography,
  Zoom,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router-dom';
import Login from '../../api/login';
import logoSrc from '../../images/logo.png';
import {
  consumeDashboardTokenFromUrl,
  hasDashboardAuthSession,
  saveDashboardAuthSession,
  setDashboardSessionId,
} from '../../utils/dashboardAuth';
import './Landing.css';

export default function Landing() {
  const [email, setEmail] = useState('');
  const [otpReceived, setOtpReceived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  useEffect(() => {
    const consumed = consumeDashboardTokenFromUrl();
    if (consumed || hasDashboardAuthSession()) {
      navigate('/dashboard/manage', { replace: true });
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (otpReceived && otpRefs[0].current) {
      otpRefs[0].current.focus();
    }
  }, [otpReceived]);

  function sendOtp() {
    setLoading(true);
    setError(null);
    Login.sendOtp(email)
      .then((response) => {
        const heyduSessionID = response.headers['heydu-session-id'];
        setDashboardSessionId(heyduSessionID);
        setOtpReceived(true);
        setSuccess('OTP sent successfully! Check your email.');
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.response?.data?.message || 'Failed to send OTP');
      });
  }

  function validateOtp() {
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError(null);
    Login.validateOtp(email, otpValue)
      .then((response) => {
        saveDashboardAuthSession(response.data, email);
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/dashboard/manage');
        }, 500);
      })
      .catch((err) => {
        setLoading(false);
        setError(err?.response?.data?.message || 'Invalid OTP');
        setOtp(['', '', '', '', '', '']);
        if (otpRefs[0].current) {
          otpRefs[0].current.focus();
        }
      });
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError(null);

    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }

    if (newOtp.every((digit) => digit !== '') && newOtp.join('').length === 6) {
      setTimeout(() => {
        validateOtp();
      }, 100);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    otpRefs[lastFilledIndex].current?.focus();
  };

  const handleBackToEmail = () => {
    setOtpReceived(false);
    setOtp(['', '', '', '', '', '']);
    setError(null);
    setSuccess('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: '#0a0e27',
      }}
    >
      {/* Background effects */}
      <Box
        className="graffiti-bg"
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 30%, rgba(102, 126, 234, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(118, 75, 162, 0.15) 0%, transparent 50%)',
            animation: 'pulse-bg 8s ease-in-out infinite',
          },
        }}
      >
        {/* Grid overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(102, 126, 234, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(102, 126, 234, 0.03) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
            opacity: 0.5,
          }}
        />

        {/* Spray effects */}
        <Box
          className="spray-1"
          sx={{
            position: 'absolute',
            width: '700px',
            height: '700px',
            top: '-150px',
            right: '-100px',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.25) 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: 0.6,
          }}
        />
        <Box
          className="spray-2"
          sx={{
            position: 'absolute',
            width: '600px',
            height: '600px',
            bottom: '-100px',
            right: '20%',
            background: 'radial-gradient(circle, rgba(118, 75, 162, 0.25) 0%, transparent 70%)',
            filter: 'blur(80px)',
            opacity: 0.6,
          }}
        />
      </Box>

      {/* Left side - Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%', lg: '40%' },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, sm: 4, md: 6 },
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Zoom in={true} timeout={600}>
          <Box sx={{ width: '100%', maxWidth: '480px' }}>
            {/* Login Card */}
            <Box
              className="login-card"
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: '24px',
                background: 'rgba(15, 20, 40, 0.95)',
                backdropFilter: 'blur(40px)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                boxShadow: '0 20px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(102, 126, 234, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s linear infinite',
                },
              }}
            >
              {/* Logo and Header */}
              <Fade in={true} timeout={800}>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <img
                      src={logoSrc}
                      alt="HeyDu Logo"
                      style={{
                        width: '80px',
                        height: 'auto',
                        filter: 'drop-shadow(0 4px 16px rgba(102, 126, 234, 0.4))',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #ffffff 0%, #a0aec0 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                    }}
                  >
                    Admin Portal
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.95rem',
                    }}
                  >
                    {!otpReceived
                      ? 'Enter your credentials to access the dashboard'
                      : 'Verify your identity with the OTP'}
                  </Typography>
                </Box>
              </Fade>

              {/* Error/Success Alert */}
              {(error || success) && (
                <Fade in={true}>
                  <Alert
                    severity={error ? 'error' : 'success'}
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      bgcolor: error ? 'rgba(211, 47, 47, 0.15)' : 'rgba(46, 125, 50, 0.15)',
                      color: error ? '#ff6b6b' : '#69db7c',
                      border: `1px solid ${error ? 'rgba(211, 47, 47, 0.3)' : 'rgba(46, 125, 50, 0.3)'}`,
                      '& .MuiAlert-icon': {
                        color: error ? '#ff6b6b' : '#69db7c',
                      },
                    }}
                    onClose={() => {
                      setError(null);
                      setSuccess('');
                    }}
                  >
                    {error || success}
                  </Alert>
                </Fade>
              )}

              {/* Email Form */}
              {!otpReceived ? (
                <Fade in={!otpReceived} timeout={600}>
                  <Box
                    component="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendOtp();
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      required
                      autoFocus
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined sx={{ color: '#667eea' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(102, 126, 234, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(102, 126, 234, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#667eea',
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.6)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#667eea',
                        },
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={loading || !email}
                      endIcon={loading ? null : <ArrowForward />}
                      sx={{
                        py: 1.75,
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 6px 24px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'rgba(102, 126, 234, 0.3)',
                          color: 'rgba(255, 255, 255, 0.5)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? <ThreeDots height="24" width="40" color="white" /> : 'Send OTP'}
                    </Button>
                  </Box>
                </Fade>
              ) : (
                /* OTP Form */
                <Fade in={otpReceived} timeout={600}>
                  <Box>
                    {/* Back Button */}
                    <Box sx={{ mb: 3 }}>
                      <Button
                        startIcon={<ArrowBack />}
                        onClick={handleBackToEmail}
                        disabled={loading}
                        sx={{
                          textTransform: 'none',
                          color: 'rgba(255, 255, 255, 0.6)',
                          '&:hover': {
                            background: 'rgba(102, 126, 234, 0.1)',
                            color: 'white',
                          },
                        }}
                      >
                        Change Email
                      </Button>
                    </Box>

                    {/* Email Display */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: '12px',
                        background: 'rgba(102, 126, 234, 0.1)',
                        border: '1px solid rgba(102, 126, 234, 0.3)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          color: 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        OTP sent to
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#667eea' }}>
                        {email}
                      </Typography>
                    </Box>

                    {/* OTP Input Boxes */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          justifyContent: 'center',
                          mb: 2,
                        }}
                        onPaste={handleOtpPaste}
                      >
                        {otp.map((digit, index) => (
                          <TextField
                            key={index}
                            inputRef={otpRefs[index]}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            disabled={loading}
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                padding: '14px 0',
                                color: 'white',
                              },
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                            }}
                            sx={{
                              width: '54px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                background: digit
                                  ? 'rgba(102, 126, 234, 0.15)'
                                  : 'rgba(255, 255, 255, 0.05)',
                                '& fieldset': {
                                  borderWidth: '2px',
                                  borderColor: digit ? '#667eea' : 'rgba(102, 126, 234, 0.3)',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#667eea',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#667eea',
                                  boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.2)',
                                },
                              },
                              transition: 'all 0.2s ease',
                            }}
                          />
                        ))}
                      </Box>

                      <Box sx={{ textAlign: 'center' }}>
                        <Button
                          onClick={sendOtp}
                          disabled={loading}
                          size="small"
                          sx={{
                            textTransform: 'none',
                            color: '#667eea',
                            fontSize: '0.875rem',
                            '&:hover': {
                              background: 'rgba(102, 126, 234, 0.1)',
                            },
                          }}
                        >
                          Resend OTP
                        </Button>
                      </Box>
                    </Box>

                    {/* Verify Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={validateOtp}
                      disabled={loading || otp.some((digit) => !digit)}
                      endIcon={loading ? null : <CheckCircleOutline />}
                      sx={{
                        py: 1.75,
                        borderRadius: '12px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 6px 24px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'rgba(102, 126, 234, 0.3)',
                          color: 'rgba(255, 255, 255, 0.5)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? (
                        <ThreeDots height="24" width="40" color="white" />
                      ) : (
                        'Verify & Login'
                      )}
                    </Button>
                  </Box>
                </Fade>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                  }}
                >
                  <LockOutlined sx={{ fontSize: '14px' }} />
                  Secured with end-to-end encryption
                </Typography>
              </Box>
            </Box>
          </Box>
        </Zoom>
      </Box>

      {/* Right side - Graffiti Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: '100vh',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
          padding: 6,
        }}
      >
        {/* Main graffiti content */}
        <Box sx={{ position: 'relative', width: '100%', maxWidth: '600px' }}>
          {/* Large "ACCESS" text */}
          <Fade in={true} timeout={1000}>
            <Typography
              className="graffiti-main-text"
              sx={{
                fontSize: { md: '140px', lg: '180px' },
                fontWeight: 900,
                color: 'transparent',
                WebkitTextStroke: '3px rgba(102, 126, 234, 0.25)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                lineHeight: 1,
                mb: 2,
                userSelect: 'none',
                position: 'relative',
                '&::after': {
                  content: '"ACCESS"',
                  position: 'absolute',
                  left: '8px',
                  top: '8px',
                  WebkitTextStroke: '2px rgba(118, 75, 162, 0.15)',
                  zIndex: -1,
                },
              }}
            >
              ACCESS
            </Typography>
          </Fade>

          {/* "GRANTED" subtitle */}
          <Fade in={true} timeout={1200}>
            <Typography
              className="graffiti-sub-text"
              sx={{
                fontSize: { md: '60px', lg: '80px' },
                fontWeight: 900,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 6,
                userSelect: 'none',
                opacity: 0.8,
              }}
            >
              GRANTED
            </Typography>
          </Fade>

          {/* Feature badges */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[
              {
                Icon: School,
                label: 'Manage Institutions',
                desc: 'Oversee colleges & universities',
                delay: 0,
              },
              {
                Icon: Assignment,
                label: 'Approve Requests',
                desc: 'Review and process applications',
                delay: 0.2,
              },
              {
                Icon: People,
                label: 'Student & Staff Data',
                desc: 'Centralized user management',
                delay: 0.4,
              },
              {
                Icon: Analytics,
                label: 'Education Analytics',
                desc: 'Track performance & insights',
                delay: 0.6,
              },
            ].map(({ Icon, label, desc, delay }, idx) => (
              <Fade key={idx} in={true} timeout={1000} style={{ transitionDelay: `${delay}s` }}>
                <Box
                  className="feature-badge"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    p: 2.5,
                    borderRadius: '16px',
                    background: 'rgba(15, 20, 40, 0.6)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    '&:hover': {
                      background: 'rgba(15, 20, 40, 0.8)',
                      transform: 'translateX(10px)',
                      borderColor: 'rgba(102, 126, 234, 0.4)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: 'white',
                        mb: 0.5,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      {desc}
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            ))}
          </Box>

          {/* Floating admin icon */}
          <Box
            className="floating-admin-icon"
            sx={{
              position: 'absolute',
              top: '-80px',
              right: '-60px',
              width: 200,
              height: 200,
              opacity: 0.1,
            }}
          >
            <AdminPanelSettings
              sx={{
                fontSize: 200,
                color: '#667eea',
                animation: 'float-slow 8s ease-in-out infinite',
              }}
            />
          </Box>

          {/* Decorative spray circles */}
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              className="spray-circle"
              sx={{
                position: 'absolute',
                width: `${40 + i * 15}px`,
                height: `${40 + i * 15}px`,
                borderRadius: '50%',
                border: `2px solid rgba(${i % 2 === 0 ? '102, 126, 234' : '118, 75, 162'}, ${0.1 - i * 0.015})`,
                top: `${20 + i * 60}px`,
                right: `${-20 - i * 10}px`,
                animation: `rotate-slow ${10 + i * 2}s linear infinite ${i % 2 === 0 ? '' : 'reverse'}`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
